import { DataTable, Spinner } from "@shopify/polaris";
import { useEffect, useRef } from "react";

import { LoadingContainer, NoCancellationData, UserTableWrapper } from "../../CancellationReasonsSettings/styles";
import { InterceptOutcomesTabConfig } from "./types";

export const getInterceptOutcomesTab = (isLoadingData: boolean, config: InterceptOutcomesTabConfig) => ({
  id: config.id,
  value: <InterceptOutcomesTabValue isLoadingData={isLoadingData} config={config} />,
  accessibilityLabel: config.label,
  content: <span data-testid="cancellation-intercept-outcome-tab">{config.label}</span>,
});

function InterceptOutcomesTabValue({
  isLoadingData,
  config,
}: {
  isLoadingData: boolean;
  config: InterceptOutcomesTabConfig;
}) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const { hasMore, onLoadMore } = config;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || typeof IntersectionObserver === "undefined") {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          onLoadMore();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore]);

  if (isLoadingData) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  if (!config.rows?.length) {
    return (
      <NoCancellationData>
        <p>{config.emptyMessage}</p>
      </NoCancellationData>
    );
  }

  return (
    <UserTableWrapper data-testid="cancellation-intercept-outcome-panel">
      <DataTable
        columnContentTypes={["text", "numeric", "text", "text"]}
        headings={config.headings}
        rows={config.rows}
        sortable={[false, false, false, false]}
        defaultSortDirection="descending"
        initialSortColumnIndex={3}
      />
      {!!config.hasMore && <div ref={sentinelRef} />}
      {!!config.isLoadingMore && (
        <LoadingContainer>
          <Spinner size="small" />
        </LoadingContainer>
      )}
    </UserTableWrapper>
  );
}
